import React, { useEffect, useState } from "react";
import { styled } from "@mui/material/styles";
import MuiDrawer from "@mui/material/Drawer";
import Skeleton from "@mui/material/Skeleton";
import CustomizedList from "../List";
import { useSelector } from "react-redux";
import InnoIcons from "InnoUI/innoicons";
import { useNavigate } from "react-router";
import Folder from "./Folder";
import theme from "theme";
import { apiGet } from "api/callApi";
import { projectType } from "utils/lookUpTable";
import { useSearchParams } from "react-router-dom";
import TooltipText from "components/TooltipText";
import SC from "./style";
import { OccupantInformationCard } from "@components/";
import useIsMobile from "customHook/useIsMobile";
import { isEmpty } from "utils/helper";
const drawerWidth = 300;
const openedMixin = (theme) => ({
  width: drawerWidth,
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflowX: "hidden",
  backgroundColor: "#F7F9F7",
  marginTop: "65px",
  [theme.breakpoints.down("sm")]: {
    marginTop: "58px",
  },
  [theme.breakpoints.down("lg")]: {
    width: 180,
  },
});
const closedMixin = (theme) => ({
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  overflowX: "hidden",
  marginTop: "65px",
  backgroundColor: "#F7F9F7",
  width: `60px`,
  [theme.breakpoints.down("sm")]: {
    marginTop: "58px",
  },
});

const Drawer = styled(MuiDrawer, {
  shouldForwardProp: (prop) => prop !== "open",
})(({ theme, open }) => ({
  width: drawerWidth,
  flexShrink: 0,
  whiteSpace: "nowrap",
  boxSizing: "border-box",
  ...(open && {
    ...openedMixin(theme),
    "& .MuiDrawer-paper": {
      ...openedMixin(theme),
    },
  }),
  ...(!open && {
    ...closedMixin(theme),
    "& .MuiDrawer-paper": {
      ...closedMixin(theme),
    },
  }),
}));

const UserDrawer = ({ navItems }) => {
  const [open, setOpen] = useState(true);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true); // Loading state
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const type = searchParams.get("type");
  const isManage = searchParams.get("isManage");
  const currentRouteFor = searchParams.get("projectId");
  const isMobile = useIsMobile();

  const currentProject = useSelector(
    (state) => state?.currentProject?.data || []
  );
  const projectError = useSelector((state) => state?.currentProject?.error);
  const currentOccupant = useSelector(
    (state) => state?.currentOccupant?.data || []
  );
  const occupantError = useSelector((state) => state?.currentOccupant?.error);

  const accessControlData = useSelector(
    (state) => state?.accessControlData?.data || []
  );

  const routeType = isEmpty(type) ? String(projectType.property) : String(type);
  const isPropertyRoute = routeType === String(projectType.property);
  const isOccupantRoute = routeType === String(projectType.occupant);
  const routeHasProjectId = !isEmpty(currentRouteFor);
  const isProjectRouteReady =
    isPropertyRoute &&
    currentProject?.id &&
    (!routeHasProjectId ||
      String(currentProject.id) === String(currentRouteFor));
  const isOccupantRouteReady =
    isOccupantRoute &&
    currentOccupant?.id &&
    (!routeHasProjectId ||
      String(currentOccupant.id) === String(currentRouteFor));
  const templateProjectId = isProjectRouteReady
    ? currentProject.id
    : isOccupantRouteReady
    ? currentOccupant.id
    : null;

  const drawerTitle = isOccupantRoute
    ? currentOccupant?.occupantName && currentOccupant?.occupantNo
      ? `${currentOccupant.occupantName} - ${currentOccupant.occupantNo}`
      : occupantError
      ? "Occupant not found"
      : ""
    : currentProject?.propertyName ||
      (projectError ? "Project not found" : "");

  const fetchTemplateData = async () => {
    try {
      setLoading(true);
      let endpoint;
      if (isPropertyRoute && templateProjectId) {
        endpoint = `/v1/projects/projecttemplate/${templateProjectId}/${projectType.property}`;
      } else if (isOccupantRoute && templateProjectId) {
        endpoint = `/v1/projects/projecttemplate/${templateProjectId}/${projectType.occupant}`;
      } else {
        setData([]);
        setLoading(false);
        return;
      }
      const result = await apiGet(endpoint);
      setData(result.data);
    } catch (err) {
      console.error("Error fetching template data:", err);
    } finally {
      setLoading(false); // End loading
    }
  };

  useEffect(() => {
    fetchTemplateData();
  }, [isOccupantRoute, isPropertyRoute, templateProjectId]);

  const handleClick = (href) => {
    navigate(`/project-home`);
  };

  const handleManageClick = () => {
    if (isManage == "true") {
      navigate(-1);
      return;
    }
    if (!templateProjectId) return;

    navigate(
      `/manage-template?type=${routeType}&isManage=${true}&projectId=${
        isPropertyRoute ? currentProject.id : currentOccupant.id
      }`,
      {
        state: { data: isPropertyRoute ? currentProject : currentOccupant },
      }
    );
  };

  useEffect(() => {
    if (isMobile) {
      setOpen(false);
    }
  }, [isMobile]);

  return (
    <Drawer variant="permanent" open={open}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "12px 10px",
          backgroundColor: "#F7F9F7",
          flexShrink: 0,
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            width: "100%",
          }}
        >
          <InnoIcons.MenuIcon
            sx={{
              cursor: "pointer",
              fontSize: "24px",
              marginRight: open ? "10px" : "0",
              color: theme.colors.themeColor,
            }}
            onClick={() => setOpen((prev) => !prev)}
          />

          {open && (
            <p
              style={{
                margin: "0 0 0 10px",
                fontWeight: "bold",
                fontSize: "12px",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                width: "calc(100% - 40px)",
                cursor: "default",
              }}
              onClick={handleClick}
            >
              {drawerTitle ? (
                <TooltipText title={drawerTitle} maxLength={30} />
              ) : (
                <Skeleton variant="text" width={120} height={18} />
              )}
            </p>
          )}
        </div>
      </div>
      {open && (
        <>
          <div
            style={{
              backgroundColor: "white",
              padding: "12px 0px 12px 20px",
              borderBottom: `1px solid ${theme.colors.bordertask}`,
              flexShrink: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <span
              style={{
                fontSize: "14px",
                fontWeight: "bold",
                whiteSpace: "nowrap",
                overflow: "hidden",
              }}
            >
              Folders/Files
            </span>

            {/* Manage Button */}
            {accessControlData.canManageProjectTemplate == 1 &&
            templateProjectId ? (
              <div
                style={{
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                  padding: "6px 10px",
                  height: "28px",
                  // border: `1px solid ${theme.colors.themeColor}`,
                  borderRadius: "50px 0px 0px 50px",
                  background: theme.gradient.themeGradient,
                  color: theme.colors.white,
                  transition: "all 0.2s ease-in-out",
                  marginRight: "10px",
                }}
                onClick={handleManageClick}
              >
                <InnoIcons.CreateNewFolderOutlinedIcon
                  style={{ fontSize: "16px" }}
                />
                <span
                  className="manage-text"
                  style={{
                    overflow: "hidden",
                    whiteSpace: "nowrap",
                    fontSize: "13px",
                    fontWeight: 500,
                    maxWidth: "100px",
                  }}
                >
                  Manage
                </span>
              </div>
            ) : (
              ""
            )}
          </div>

          <SC.ChildContainer>
            {loading ? (
              Array.from({ length: 10 }).map((_, index) => (
                <Skeleton
                  key={index}
                  variant="rectangular"
                  height={44}
                  style={{
                    margin: "8px 15px",
                    borderRadius: "8px",
                  }}
                />
              ))
            ) : (
              <>
                {data.map((folder) => (
                  <Folder key={folder.id} folder={folder} />
                ))}
                {currentOccupant?.occupantOpeningDate && isOccupantRoute && (
                  <OccupantInformationCard />
                )}
              </>
            )}
          </SC.ChildContainer>
          <CustomizedList items={navItems} open={open} />
        </>
      )}
    </Drawer>
  );
};

export default UserDrawer;
